// Combined setup + input screen: pick the batch noyau, paste the words, analyse.
// (The old separate "mode" and "input method" steps were collapsed into this one
// page in the rework — paste is the only input, forced.) "Analyser" is disabled
// until the input has non-whitespace content.
//
// A draft is now available from this phase already: as soon as there's content
// (text or a picked noyau), a session is created in 'input' status and
// debounce-autosaved on every change, before "Analyser" is ever clicked. `session`
// is passed in when resuming such a draft from the entry cards. "Enregistrer et
// quitter" flushes that save immediately (so it doesn't race the debounce on a
// quick exit); "Annuler" fully discards the draft, deleting it server-side too.
//
// The paste field is a CodeMirror editor (not a plain textarea) so malformed
// lines — an empty trailing tag group `Mot ()`, or unbalanced/nested parens the
// parser can't cleanly strip — get a red underline via bulkLintSource. This is
// advisory only: it never blocks "Analyser", the parser already tolerates these
// lines (just parsing them worse). Hovering/clicking the underline shows the
// message with a one-click "Corriger" action that rewrites the line.
import { NoyauPicker } from '@/components/words/bulk-add/bulk-noyau-picker';
import { WordShellTopbar } from '@/components/words/word-shell-topbar';
import { accentStyle } from '@/lib/bulk-add/accent';
import { bulkLintSource } from '@/lib/bulk-add/bulk-lint';
import { useBulkAdd } from '@/lib/bulk-add/bulk-session';
import type { BulkSession } from '@/lib/bulk-add/types';
import type { NoyauCode } from '@/schemas/word';
import { linter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { useEffect, useMemo, useRef, useState } from 'react';

/** No line numbers/gutters — the editor should read like the plain paste field it replaces. */
const BASIC_SETUP = {
  lineNumbers: false,
  foldGutter: false,
  highlightActiveLine: false,
  highlightActiveLineGutter: false,
};

interface BulkInputProps {
  /** An 'input'-status draft being resumed from the entry cards, if any. */
  session?: BulkSession;
}

/** The bulk-add input page (noyau picker + paste field + Analyser). */
export function BulkInput({ session }: BulkInputProps) {
  const bulk = useBulkAdd();
  const [text, setText] = useState(session?.text ?? '');
  const [noyau, setNoyau] = useState<NoyauCode | null>(session?.noyau ?? null);
  const draftId = useRef<string | null>(session?.id ?? null);

  const canAnalyze = text.trim().length > 0;
  const extensions = useMemo(
    () => [
      EditorView.lineWrapping,
      linter(bulkLintSource),
      // CodeMirror hardcodes spellcheck="false" on its editable surface — the
      // plain <textarea> this replaced got the browser's native spellcheck
      // (red underline + right-click suggestions) for free, so restore it here.
      EditorView.contentAttributes.of({ spellcheck: 'true', autocorrect: 'on' }),
    ],
    [],
  );

  const { updateInputDraft } = bulk;
  useEffect(() => {
    if (text.trim().length === 0 && noyau === null) return;
    draftId.current = updateInputDraft(draftId.current, text, noyau);
  }, [text, noyau, updateInputDraft]);

  function handleAnalyze() {
    if (!canAnalyze) return;
    void bulk.analyze(text, noyau, draftId.current);
  }

  // "Annuler" discards: if typing already minted a draft (autosave creates one
  // on first content), delete it server-side too — this is a full discard, not
  // just leaving the screen. Plain navigate-away with no draft just resets.
  function handleCancel() {
    if (draftId.current) bulk.cancelSession(draftId.current);
    else bulk.cancelInput();
  }

  // "Enregistrer et quitter" flushes the pending debounced save immediately
  // (matching BulkReview's button of the same name) so leaving right after
  // typing doesn't race the 1s autosave debounce.
  async function handleSaveAndClose() {
    if (draftId.current) await bulk.saveAndClose(draftId.current);
    else bulk.cancelInput();
  }

  return (
    <>
      <WordShellTopbar />
      <div className="bulk" style={accentStyle(noyau)}>
        <div className="bulk-input">
          <header className="bulk-input__head">
            <h1 className="bulk-input__title">Nouvelle revue d'ajout</h1>
            <p className="bulk-input__subtitle">
              Choisissez le noyau du lot, puis collez un mot par ligne. Format
              <code> Mot (tag)</code> pour pré-remplir des tags.
            </p>
          </header>

          <section className="bulk-input__section">
            <h2 className="bulk-input__label">Noyau du lot</h2>
            <NoyauPicker value={noyau} onChange={setNoyau} />
          </section>

          <section className="bulk-input__section">
            <h2 className="bulk-input__label">Mots à ajouter</h2>
            <CodeMirror
              className="bulk-input__editor"
              value={text}
              onChange={setText}
              extensions={extensions}
              basicSetup={BASIC_SETUP}
              height="220px"
              placeholder={'Un mot par ligne…\nSouveraineté\nLiberté (politique, droit)'}
              aria-label="Mots à ajouter, un par ligne"
            />
          </section>

          <div className="bulk-input__actions">
            <button type="button" className="bulk-btn bulk-btn--ghost" onClick={handleCancel}>
              Annuler
            </button>
            <button
              type="button"
              className="bulk-btn bulk-btn--outline"
              onClick={() => void handleSaveAndClose()}
            >
              Enregistrer et quitter
            </button>
            <button
              type="button"
              className="bulk-btn bulk-btn--primary"
              disabled={!canAnalyze}
              onClick={handleAnalyze}
            >
              Analyser
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
